import type { Engine } from '@nebulark/engine';
import type { Snapshot, EngineEvent } from '@nebulark/engine';
import type { TouchInput } from './input/touch.js';

const FIXED_DT = 1000 / 60;
const MAX_CATCH_UP = 5;

export type TickCallback = (snapshot: Snapshot, events: EngineEvent[], dtMs: number) => void;

export class GameLoop {
  private rafId = 0;
  private lastTime = 0;
  private accumulator = 0;
  private running = false;

  constructor(
    private readonly engine: Engine,
    private readonly touch: TouchInput,
    private readonly onTick: TickCallback,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.frame);

    const realDt = Math.min(now - this.lastTime, 200); // cap at 200ms to avoid spiral
    this.lastTime = now;
    this.accumulator += realDt;

    let ticks = 0;
    while (this.accumulator >= FIXED_DT && ticks < MAX_CATCH_UP) {
      const input = this.touch.getCurrentInput();
      this.engine.tick(FIXED_DT, [input]);
      this.touch.consumeBomb();
      this.accumulator -= FIXED_DT;
      ticks++;
    }

    const snap = this.engine.getSnapshot();
    const events = this.engine.drainEvents();
    this.onTick(snap, events, realDt);
  };
}
